import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, X, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateChapterContent } from '../lib/ai-config';

const toneOptions = [
  'Formal',
  'Casual',
  'Academic',
  'Conversational',
  'Professional but friendly',
  'Storytelling',
] as const;

const perspectives = [
  'First-person',
  'Second-person',
  'Third-person limited',
  'Third-person omniscient',
] as const;


const bookSettingsSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  author: z.string().optional(),
  author_bio: z.string().optional(),
  audience: z.string().optional(),
  perspective: z.enum(perspectives),
  goals: z.string().optional(),
  disclaimers: z.string().optional(),
  rules: z.string().optional(),
  writing_style_sample: z.string().optional(),
  style_notes: z.string().optional(),
  preferred_tone: z.array(z.enum(toneOptions)).default(['Professional but friendly']),
  target_word_count: z.number().min(100).max(10000).default(1000),
  regenerate_content: z.boolean().default(false),
});

type BookSettingsFormData = z.infer<typeof bookSettingsSchema>;

interface BookSettingsEditorProps {
  bookId: string;
  initialData: BookSettingsFormData;
  onClose: () => void;
  onSaved: () => void;
}

export function BookSettingsEditor({ bookId, initialData, onClose, onSaved }: BookSettingsEditorProps) {
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; status?: string } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BookSettingsFormData>({
    resolver: zodResolver(bookSettingsSchema),
    defaultValues: {
      ...initialData,
      regenerate_content: false,
    },
  });

  const regenerateContent = watch('regenerate_content');

  const onSubmit = async (data: BookSettingsFormData) => {
    try {
      setSaving(true);

      // First update the book metadata
      const { error: updateError } = await supabase
        .from('books_metadata')
        .update({
          title: data.title,
          subtitle: data.subtitle,
          author: data.author,
          author_bio: data.author_bio,
          audience: data.audience,
          perspective: data.perspective,
          goals: data.goals,
          disclaimers: data.disclaimers,
          rules: data.rules,
          writing_style_sample: data.writing_style_sample,
          style_notes: data.style_notes,
          preferred_tone: data.preferred_tone,
          ai_model: 'claude-sonnet-4-6',
          target_word_count: data.target_word_count,
        })
        .eq('book_id', bookId)
        .select()
        .single();

      if (updateError) throw updateError;

      // If regenerate_content is checked, regenerate all chapters
      if (data.regenerate_content) {
        // Get all chapters
        const { data: chapters, error: chaptersError } = await supabase
          .from('books_chapters')
          .select('*')
          .eq('book_id', bookId)
          .order('order_index');

        if (chaptersError) throw chaptersError;

        setProgress({ current: 0, total: chapters.length });

        // Regenerate each chapter
        for (let i = 0; i < chapters.length; i++) {
          const chapter = chapters[i];
          const chapterType = chapter.chapter_type || 'chapter';
          const chapterNumber = chapterType === 'chapter' ? chapter.chapter_number : null;
          const chapterTitle = chapterType === 'chapter' && chapterNumber
            ? `Chapter ${chapterNumber}: ${chapter.chapter_title}`
            : chapter.chapter_title;

          setProgress({ current: i + 1, total: chapters.length, status: `Writing "${chapterTitle}"...` });
          // Yield to event loop so React can render the progress update
          await new Promise((r) => setTimeout(r, 0));

          try {
            const generatedContent = await generateChapterContent({
              model: data.ai_model,
              bookTitle: data.title,
              audience: data.audience || '',
              perspective: data.perspective,
              goals: data.goals || '',
              disclaimers: data.disclaimers,
              rules: data.rules || '',
              writingStyleSample: data.writing_style_sample,
              styleNotes: data.style_notes,
              preferredTone: data.preferred_tone,
              chapterTitle,
              chapterType: chapterType as any,
              chapterNotes: chapter.notes,
              target_word_count: data.target_word_count,
              chapterReferences: chapter.chapter_references,
            });

            const { error: updateChapterError } = await supabase
              .from('books_chapters')
              .update({
                generated_text: generatedContent,
                status: 'completed'
              })
              .eq('chapter_id', chapter.chapter_id);

            if (updateChapterError) throw updateChapterError;
          } catch (error) {
            console.error(`Error regenerating chapter "${chapter.chapter_title}":`, error);
            throw new Error(`Failed to regenerate chapter "${chapter.chapter_title}"`);
          }
        }
      }

      onSaved();
    } catch (error) {
      console.error('Error saving book settings:', error);
      alert('Failed to save book settings. Please try again.');
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Edit Book Settings
              </h3>

              {progress && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                    <div
                      className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500 ease-in-out"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    {progress.status || `Regenerating chapter ${progress.current} of ${progress.total}`}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Book Title
                    </label>
                    <input
                      type="text"
                      {...register('title')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700">
                      Subtitle (Optional)
                    </label>
                    <input
                      type="text"
                      {...register('subtitle')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                      Author Name (Optional)
                    </label>
                    <input
                      type="text"
                      {...register('author')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="author_bio" className="block text-sm font-medium text-gray-700">
                      About the Author (Optional)
                    </label>
                    <textarea
                      {...register('author_bio')}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="Write a compelling biography that will appear in your book"
                    />
                  </div>

                  <div>
                    <label htmlFor="audience" className="block text-sm font-medium text-gray-700">
                      Target Audience
                    </label>
                    <input
                      type="text"
                      {...register('audience')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="perspective" className="block text-sm font-medium text-gray-700">
                      Writing Perspective
                    </label>
                    <select
                      {...register('perspective')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      {perspectives.map(perspective => (
                        <option key={perspective} value={perspective}>
                          {perspective}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="target_word_count" className="block text-sm font-medium text-gray-700">
                      Target Words per Chapter
                    </label>
                    <input
                      type="number"
                      {...register('target_word_count', { valueAsNumber: true })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      min={100}
                      max={10000}
                    />
                    {errors.target_word_count && (
                      <p className="mt-1 text-sm text-red-600">{errors.target_word_count.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Set the target word count for each chapter (between 100 and 10,000 words)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Preferred Tone
                    </label>
                    <div className="mt-2 space-y-2">
                      {toneOptions.map((tone) => (
                        <label key={tone} className="inline-flex items-center mr-4">
                          <input
                            type="checkbox"
                            value={tone}
                            {...register('preferred_tone')}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{tone}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="goals" className="block text-sm font-medium text-gray-700">
                      Book Goals
                    </label>
                    <textarea
                      {...register('goals')}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="disclaimers" className="block text-sm font-medium text-gray-700">
                      Disclaimers
                    </label>
                    <textarea
                      {...register('disclaimers')}
                      rows={2}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="rules" className="block text-sm font-medium text-gray-700">
                      Writing Rules
                    </label>
                    <textarea
                      {...register('rules')}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="writing_style_sample" className="block text-sm font-medium text-gray-700">
                      Writing Style Sample
                    </label>
                    <textarea
                      {...register('writing_style_sample')}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="style_notes" className="block text-sm font-medium text-gray-700">
                      Style Notes
                    </label>
                    <textarea
                      {...register('style_notes')}
                      rows={2}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="relative flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        {...register('regenerate_content')}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="regenerate_content" className="font-medium text-gray-700">
                        Regenerate Content
                      </label>
                      <p className="text-gray-500">
                        Check this box to regenerate all chapters with the new settings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        {regenerateContent ? 'Regenerating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        {regenerateContent ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Save & Regenerate
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
